import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import { BusinessEntityActionKeys as keys } from './businessEntityActionKeys';
import { businessEntityActionTypes } from './businessEntityActionTypes';
import { IBusinessEntityMin, IBusinessEntityMinDetails } from './businessEntityInterfaces';

export interface IBusinessEntityState {
  addresses: { [index: number]: number[] };
  competitors: Record<number, number[]>;
  contacts: { [index: number]: number[] };
  loaded: { contacts: number[]; salesCalls: number[] };
  loading: { contacts: number[]; salesCalls: number[] };
  search: { [index: string]: { loadedAt: Date; items: IBusinessEntityMin[] } };
  searchLoading: string[];
  marketSegments: Record<number, number[]>;
  minDetails: Record<number, IBusinessEntityMinDetails>;
  names: { [index: number]: string };
  productCategories: Record<number, number[]>;
  salesCalls: { [index: number]: number[] };
  totalItems: {
    salesCalls: { [index: number]: number };
  };
  rankTypes: Record<number, number>;
}

const initialState: IBusinessEntityState = {
  addresses: {},
  competitors: {},
  contacts: {},
  loaded: { contacts: [], salesCalls: [] },
  loading: { contacts: [], salesCalls: [] },
  marketSegments: {},
  minDetails: {},
  names: {},
  productCategories: {},
  rankTypes: {},
  salesCalls: {},
  search: {},
  searchLoading: [],
  totalItems: { salesCalls: {} }
};

export function businessEnitityReducer(
  state: IBusinessEntityState = initialState,
  action: businessEntityActionTypes
): IBusinessEntityState {
  switch (action.type) {
    case keys.ADD_BUSINESS_ENTITY_ADDRESSES:
      return {
        ...state,
        addresses: {
          ...state.addresses,
          ...action.payload.reduce<{ [index: number]: number[] }>((hash, b) => {
            hash[b.businessEntityId] = uniqPrimativeMerge(hash[b.businessEntityId], b.addressIds);
            return hash;
          }, {})
        }
      };
    case keys.ADD_BUSINESS_ENTITY_CONTACTS:
      return {
        ...state,
        contacts: {
          ...action.payload.reduce((newHash, contact) => {
            return {
              ...newHash,
              [contact.businessEntityId]: uniqPrimativeMerge(newHash[contact.businessEntityId], contact.contactId)
            };
          }, state.contacts)
        }
      };
    case keys.ADD_BUSINESS_ENTITY_NAMES:
      return {
        ...state,
        names: {
          ...state.names,
          ...action.payload.reduce((newHash, n) => ({ ...newHash, [n.businessEntityId]: n.name }), state.names)
        }
      };
    case keys.ADD_BUSINESS_ENTITY_SALESCALLS:
      return {
        ...state,
        salesCalls: {
          ...action.payload.reduce((newHash, sc) => {
            return {
              ...newHash,
              [sc.businessEntityId]: uniqPrimativeMerge(newHash[sc.businessEntityId], sc.salesCallId)
            };
          }, state.salesCalls)
        }
      };
    case keys.DISABLE_LOADING_CONTACTS:
      return {
        ...state,
        loading: {
          ...state.loading,
          contacts: state.loading.contacts.filter(x => !action.payload.includes(x))
        }
      };
    case keys.DISABLE_LOADING_SALESCALLS:
      return {
        ...state,
        loading: {
          ...state.loading,
          salesCalls: state.loading.salesCalls.filter(x => !action.payload.includes(x))
        }
      };
    case keys.REMOVE_BUSINESS_ENTITY_CONTACT:
      return {
        ...state,
        contacts: {
          ...state.contacts,
          [action.payload.businessEntityId]: state.contacts[action.payload.businessEntityId]
            ? state.contacts[action.payload.businessEntityId].filter(c => c !== action.payload.contactId)
            : []
        }
      };
    case keys.REMOVE_BUSINESS_ENTITY_SALESCALL:
      return {
        ...state,
        salesCalls: {
          ...state.salesCalls,
          [action.payload.businessEntityId]: state.salesCalls[action.payload.businessEntityId]
            ? state.salesCalls[action.payload.businessEntityId].filter(s => s !== action.payload.salesCallId)
            : []
        }
      };
    case keys.SAVE_BUSINESS_ENTITY_ADDRESS_FULFILLED:
      return {
        ...state,
        addresses: {
          ...state.addresses,
          [action.payload.businessEntityId]: uniqPrimativeMerge(
            state.addresses[action.payload.businessEntityId],
            action.payload.address.addressId
          )
        }
      };
    case keys.SET_BE_ADDRESSES:
      return {
        ...state,
        addresses: {
          ...state.addresses,
          ...action.payload.reduce<{ [index: number]: number[] }>((hash, beAddy) => {
            hash[beAddy.businessEntityId] = uniqPrimativeMerge(
              state.addresses[beAddy.businessEntityId],
              beAddy.address.addressId
            );
            return hash;
          }, {})
        }
      };
    case keys.SET_BE_COMPETITORS:
      return {
        ...state,
        competitors: {
          ...state.competitors,
          [action.payload.beId]: action.payload.competitorIds
        }
      };
    case keys.SET_BE_MARKET_SEGMENTS:
      return {
        ...state,
        marketSegments: {
          ...state.marketSegments,
          [action.payload.beId]: action.payload.marketSegmentIds
        }
      };
    case keys.SET_BE_MIN_DETAILS:
      return {
        ...state,
        minDetails: {
          ...state.minDetails,
          [action.payload.businessEntityId]: action.payload
        }
      };
    case keys.SET_BE_PRODUCT_CATEGORIES:
      return {
        ...state,
        productCategories: {
          ...state.productCategories,
          [action.payload.beId]: action.payload.categoryIds
        }
      };
    case keys.SET_BE_RANK_TYPE:
      return action.payload.rankTypeId
        ? {
            ...state,
            rankTypes: {
              ...state.rankTypes,
              [action.payload.beId]: action.payload.rankTypeId
            }
          }
        : state;
    case keys.SET_BE_SALESCALL_TOTAL:
      return {
        ...state,
        totalItems: {
          ...state.totalItems,
          salesCalls: {
            ...state.totalItems.salesCalls,
            [action.payload.businessEntityId]: action.payload.totalItems
          }
        }
      };
    case keys.SET_BE_SEARCH_RESULTS:
      return {
        ...state,
        search: {
          ...state.search,
          [action.payload.searchTerm]: {
            items: action.payload.results,
            loadedAt: new Date()
          }
        }
      };
    case keys.SET_LOADED_CONTACTS:
      return {
        ...state,
        loaded: {
          ...state.loaded,
          contacts: Array.from(new Set([...state.loaded.contacts, ...action.payload]))
        }
      };
    case keys.SET_LOADED_SALESCALLS:
      return {
        ...state,
        loaded: {
          ...state.loaded,
          salesCalls: Array.from(new Set([...state.loaded.salesCalls, ...action.payload]))
        }
      };
    case keys.SET_LOADING_CONTACTS:
      return {
        ...state,
        loading: {
          ...state.loading,
          contacts: Array.from(new Set([...state.loading.contacts, ...action.payload]))
        }
      };
    case keys.SET_LOADING_SALESCALLS:
      return {
        ...state,
        loading: {
          ...state.loading,
          salesCalls: Array.from(new Set([...state.loading.salesCalls, ...action.payload]))
        }
      };
    case keys.TOGGLE_BE_SEARCH_LOADING:
      return {
        ...state,
        searchLoading: state.searchLoading.includes(action.payload)
          ? state.searchLoading.filter(x => x !== action.payload)
          : [...state.searchLoading, action.payload]
      };
    default:
      return state;
  }
}
